import React, { useEffect, useState } from "react";
import { Search, MessageCircle, Mail, Building2 } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quote Shared", "Won", "Closed"];

const waLink = (e) => {
  const num = String(e.phone || "").replace(/\D/g, "");
  if (!num) return null;
  const msg = `Hi ${e.name || "there"}, thank you for your corporate gifting enquiry with Wonder Parcel. We'd love to help curate options for ${e.company || "your team"}.`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};

export default function CorporateEnquiries() {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    setItems(null);
    try { const { data } = await adminApi.get("/corporate-enquiries"); setItems(data); }
    catch { setItems([]); toast.error("Could not load enquiries"); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/corporate-enquiries/${id}`, { status });
      setItems((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch { toast.error("Could not update status"); }
  };

  const filtered = (items || []).filter((e) => {
    if (!query) return true;
    const ql = query.toLowerCase();
    return [e.name, e.company, e.phone, e.occasion, e.budget, e.status].some((v) => (v || "").toString().toLowerCase().includes(ql));
  });

  return (
    <div data-testid="admin-corporate-enquiries">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl">Corporate Enquiries</h1><p className="text-sm text-wp-ink-muted">{items ? `${filtered.length} enquiry(ies)` : "Loading…"}</p></div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search enquiries…" className="w-56 rounded-wp-md pl-8" data-testid="corporate-enquiries-search" />
        </div>
      </div>

      <div className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
        {items === null ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Building2 className="mx-auto h-8 w-8 text-wp-ink-muted" />
            <p className="mt-2 text-wp-ink-muted">No corporate enquiries yet.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company / Contact</TableHead>
                <TableHead>Requirement</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Budget</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Reach out</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e) => (
                <TableRow key={e.id} data-testid={`corporate-enquiry-row-${e.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{e.company || "—"}</div>
                    <div className="text-xs text-wp-ink-muted">{e.name} · {e.phone}</div>
                    <div className="text-xs text-wp-ink-muted">{new Date(e.created_at).toLocaleDateString()}</div>
                  </TableCell>
                  <TableCell className="max-w-[260px]">
                    <div className="text-sm">{e.occasion}</div>
                    {e.message && <div className="mt-0.5 truncate text-xs text-wp-ink-muted" title={e.message}>{e.message}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{e.quantity}</TableCell>
                  <TableCell className="text-sm">{e.budget}</TableCell>
                  <TableCell>
                    <Select value={e.status || "New"} onValueChange={(v) => setStatus(e.id, v)}>
                      <SelectTrigger className="h-8 w-36 rounded-lg text-xs" data-testid={`corporate-enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st} className="text-xs">{st}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {waLink(e) && <a href={waLink(e)} target="_blank" rel="noreferrer" className="grid h-8 w-8 place-items-center rounded-lg text-wp-success hover:bg-wp-success/10" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                      {e.email && <a href={`mailto:${e.email}`} className="grid h-8 w-8 place-items-center rounded-lg text-wp-rose-strong hover:bg-wp-blush-100" title="Email"><Mail className="h-4 w-4" /></a>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
